import { isEmpty, uniq } from 'lodash';

import type { DocumentType } from '~/components/types/document-type';

const TEMPLATED_DOCUMENT_TYPE = 'templated_document';

// The flags the pickers filter on, picked off the generated contract so a rename on the API
// side breaks the build here. Kept `Partial` because most callers are untyped `.jsx` that hand
// over a loosely-shaped dict; every helper below therefore defines its own absent case.
type DocumentTypeConfig = Partial<Pick<DocumentType, 'is_public_option' | 'is_enabled' | 'is_all_lobs' | 'lobs'>> & {
  // Carried over from the DocumentCard.jsx helper these replaced. Unlike the four above, these
  // two are not on DocumentTypeResponse — they belong to other org config dicts (roles, payment
  // types, MOI methods) — so they are declared locally rather than picked off the contract.
  excluded_claim_types?: string[] | null;
  included_claim_types?: string[] | null;
};

export type DocumentTypesDict = Record<string, DocumentTypeConfig | undefined>;

/**
 * The organization document-types dict is deliberately unfiltered — it has to keep
 * resolving display names for documents of types that were since disabled or restricted.
 * Every picker is therefore responsible for filtering the dict down to what it may offer,
 * and these helpers are that filter.
 */

// A type is only pickable while the organization still configures it: a key that is
// missing from the dict has no display name to render, and a disabled one is off-limits.
export const isDocumentTypeEnabled = (documentTypesDict: DocumentTypesDict, docType: string): boolean => {
  const docTypeConfig = documentTypesDict?.[docType];
  return !!docTypeConfig && docTypeConfig.is_enabled !== false;
};

// The two flags read a missing value differently on purpose. `is_enabled` treats null as
// enabled, matching how the dict was read before these helpers existed. `is_public_option` is
// a legacy hidden flag the backend itself coerces with `bool(...)` when it exports a document
// type, so a null there is not-public on both sides.
const isPublicAndEnabledDocumentType = (documentTypesDict: DocumentTypesDict, docType: string): boolean =>
  !!documentTypesDict[docType]?.is_public_option && isDocumentTypeEnabled(documentTypesDict, docType);

export const isDocumentTypeAvailableForLob = (
  documentTypesDict: DocumentTypesDict,
  docType: string,
  lob?: string
): boolean => {
  if (!isDocumentTypeEnabled(documentTypesDict, docType)) {
    return false;
  }

  const docTypeConfig = documentTypesDict[docType] as DocumentTypeConfig;

  if (lob && docTypeConfig.excluded_claim_types?.includes(lob)) {
    return false;
  }

  if (!isEmpty(docTypeConfig.included_claim_types) && (!lob || !docTypeConfig.included_claim_types?.includes(lob))) {
    return false;
  }

  if (docTypeConfig.is_all_lobs || isEmpty(docTypeConfig.lobs)) {
    return true;
  }

  return !!lob && !!docTypeConfig.lobs?.includes(lob);
};

export const getAllowedDocumentTypes = (documentTypesDict: DocumentTypesDict, lob?: string): string[] =>
  Object.keys(documentTypesDict ?? {}).filter(
    (docType) =>
      isPublicAndEnabledDocumentType(documentTypesDict, docType) &&
      isDocumentTypeAvailableForLob(documentTypesDict, docType, lob)
  );

// `templated_document` is not a public upload option, but it is the type a document
// generated from a template gets, so the template-upload picker offers it on top of the
// public ones — for as long as the organization keeps it enabled and available for the lob.
export const getTemplateUploadDocumentTypes = (documentTypesDict: DocumentTypesDict, lob?: string): string[] => {
  const allowedDocumentTypes = getAllowedDocumentTypes(documentTypesDict, lob);

  if (
    allowedDocumentTypes.includes(TEMPLATED_DOCUMENT_TYPE) ||
    !isDocumentTypeAvailableForLob(documentTypesDict, TEMPLATED_DOCUMENT_TYPE, lob)
  ) {
    return allowedDocumentTypes;
  }

  return [...allowedDocumentTypes, TEMPLATED_DOCUMENT_TYPE];
};

// A document template spans several lines of business, so it may carry any type that at
// least one of them can use. An empty `lobs` means the template is not restricted to any,
// which leaves the public/enabled filtering as the only narrowing.
export const getAllowedDocumentTypesForLobs = (documentTypesDict: DocumentTypesDict, lobs: string[]): string[] => {
  const publicEnabledDocumentTypes = Object.keys(documentTypesDict ?? {}).filter((docType) =>
    isPublicAndEnabledDocumentType(documentTypesDict, docType)
  );

  if (isEmpty(lobs)) {
    return publicEnabledDocumentTypes;
  }

  const uniqueLobs = uniq(lobs);

  return publicEnabledDocumentTypes.filter((docType) =>
    uniqueLobs.some((lob) => isDocumentTypeAvailableForLob(documentTypesDict, docType, lob))
  );
};
